import BasicECharts from 'components/common/BasicEChart';
import Flex from 'components/common/Flex';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import { storeList } from 'components/common/StoreList';
import { PieChart } from 'echarts/charts';
import {
  GridComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import { useAppContext } from 'providers/AppProvider';
import React, { useMemo, useState } from 'react';
import {
  Badge,
  Card,
  Col,
  OverlayTrigger,
  ProgressBar,
  Row,
  Tooltip
} from 'react-bootstrap';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer
]);

const toNumber = value => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const getStoreName = storeId => {
  return (
    storeList.find(store => Number(store.value) === Number(storeId))?.key ||
    `Store ${storeId}`
  );
};

const getOptions = (getThemeColor, chartData, total, hasData) => ({
  color: chartData.map(item => item.color),
  tooltip: {
    show: hasData,
    trigger: 'item',
    padding: [8, 12],
    transitionDuration: 0,
    backgroundColor: getThemeColor('gray-100'),
    borderColor: getThemeColor('gray-300'),
    borderWidth: 1,
    textStyle: { color: getThemeColor('gray-1100') },
    formatter: params =>
      `<strong>${params.name}</strong><br/>${params.value} holds (${params.percent}%)`
  },
  series: [
    {
      name: 'Drug Holds',
      type: 'pie',
      radius: ['62%', '82%'],
      center: ['50%', '50%'],
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 10,
        borderWidth: 3,
        borderColor: getThemeColor('gray-100')
      },
      label: {
        show: true,
        position: 'center',
        formatter: () => `${total}\nOn hold`,
        fontSize: 18,
        fontWeight: 700,
        lineHeight: 24,
        color: getThemeColor('gray-1100')
      },
      emphasis: {
        scale: hasData,
        scaleSize: 8
      },
      data: chartData
    }
  ]
});

const getCycleLabel = isCycle =>
  Number(isCycle) === 1 ? 'Cycle' : 'Non-cycle';

const StoreRow = ({ store, total }) => {
  const percent = total > 0 ? Math.round((store.count * 100) / total) : 0;

  return (
    <div className="py-2">
      <Flex alignItems="center" justifyContent="between" className="mb-1">
        <div className="min-w-0">
          <div className="fw-semibold text-900 text-truncate d-flex align-items-center">
            <span className="text-truncate">{store.storeName}</span>
          </div>
          <div className="fs-11 text-500">
            Cycle holds: {store.cycleCount} | Non-cycle holds: {store.nonCycleCount}
          </div>
        </div>
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip style={{ position: 'fixed' }}>
              <div className="text-start">
                <div>
                  Cycle holds: <strong>{store.cycleCount}</strong>
                </div>
                <div>
                  Non-cycle holds: <strong>{store.nonCycleCount}</strong>
                </div>
              </div>
            </Tooltip>
          }
        >
          <Badge
            bg="200"
            className="text-primary rounded-pill fs-11 cursor-pointer"
          >
            {store.count}
          </Badge>
        </OverlayTrigger>
      </Flex>
      <ProgressBar
        now={percent}
        className="rounded-pill overflow-hidden"
        style={{ height: '0.35rem' }}
      />
    </div>
  );
};

StoreRow.propTypes = {
  store: PropTypes.shape({
    count: PropTypes.number.isRequired,
    cycleCount: PropTypes.number.isRequired,
    nonCycleCount: PropTypes.number.isRequired,
    storeName: PropTypes.string.isRequired
  }).isRequired,
  total: PropTypes.number.isRequired
};

const HoldCountsWidget = ({ data = {}, isLoading = false }) => {
  const { getThemeColor } = useAppContext();
  const [selectedStore, setSelectedStore] = useState({
    value: 'ALL',
    label: 'All Stores'
  });

  const breakdown = useMemo(
    () =>
      (Array.isArray(data.breakdown) ? data.breakdown : []).map(item => ({
        store_id: item.store_id,
        is_cycle: toNumber(item.is_cycle),
        hold_count: toNumber(item.hold_count)
      })),
    [data.breakdown]
  );

  const byStore = useMemo(
    () =>
      (Array.isArray(data.byStore) ? data.byStore : [])
        .map(store => {
          const storeBreakdown = breakdown.filter(
            item => Number(item.store_id) === Number(store.store_id)
          );

          return {
            store_id: store.store_id,
            count: toNumber(store.count),
            cycleCount:
              storeBreakdown.find(item => Number(item.is_cycle) === 1)
                ?.hold_count || 0,
            nonCycleCount:
              storeBreakdown.find(item => Number(item.is_cycle) === 0)
                ?.hold_count || 0,
            storeName: getStoreName(store.store_id)
          };
        })
        .sort((a, b) => b.count - a.count),
    [breakdown, data.byStore]
  );

  const byIsCycle = useMemo(
    () =>
      (Array.isArray(data.byIsCycle) ? data.byIsCycle : []).map(item => ({
        is_cycle: toNumber(item.is_cycle),
        count: toNumber(item.count)
      })),
    [data.byIsCycle]
  );

  const selectedStoreBreakdown = useMemo(() => {
    if (selectedStore.value === 'ALL') return byIsCycle;

    return breakdown
      .filter(item => String(item.store_id) === String(selectedStore.value))
      .map(item => ({
        is_cycle: item.is_cycle,
        count: item.hold_count
      }));
  }, [breakdown, byIsCycle, selectedStore]);

  const total = useMemo(() => {
    if (selectedStore.value === 'ALL') {
      return (
        toNumber(data.total) ||
        byStore.reduce((sum, store) => sum + store.count, 0)
      );
    }

    return selectedStoreBreakdown.reduce((sum, item) => sum + item.count, 0);
  }, [byStore, data.total, selectedStore, selectedStoreBreakdown]);

  const cycleCount =
    selectedStoreBreakdown.find(item => Number(item.is_cycle) === 1)?.count ||
    0;
  const nonCycleCount =
    selectedStoreBreakdown.find(item => Number(item.is_cycle) === 0)?.count ||
    0;
  const cyclePercent = total > 0 ? Math.round((cycleCount * 100) / total) : 0;
  const hasData = total > 0;
  const chartData = hasData
    ? [
        {
          name: 'Non-cycle',
          value: nonCycleCount,
          color: getThemeColor('warning')
        },
        {
          name: 'Cycle',
          value: cycleCount,
          color: getThemeColor('info')
        }
      ]
    : [
        {
          name: 'No Holds',
          value: 1,
          color: getThemeColor('gray-300')
        }
      ];

  const storeOptions = [
    { value: 'ALL', label: 'All Stores' },
    ...byStore.map(store => ({
      value: String(store.store_id),
      label: store.storeName
    }))
  ];

  return (
    <Card className="h-100 overflow-hidden shadow-sm">
      <Card.Header className="pb-0">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <h6
            className="mb-0 mt-1 text-900"
            style={{ lineHeight: 1.15, maxWidth: '7rem' }}
          >
            Drugs on Hold
          </h6>
          {/* <div style={{ minWidth: '160px' }}>
            <div className="fs-11 text-500 fw-semibold mb-1 text-end">
              <FontAwesomeIcon icon="filter" className="me-1" />
              Filter by store
            </div>
            <ThemedSelect
              options={storeOptions}
              value={selectedStore}
              onChange={option => setSelectedStore(option || storeOptions[0])}
              placeholder="Filter by store"
              isSearchable={false}
              size="sm"
            />
          </div> */}
        </div>
      </Card.Header>

      <Card.Body className="pt-3">
        {isLoading ? (
          <div className="py-4 text-center text-500">
            <FontAwesomeIcon icon="spinner" spin className="me-2" />
            Loading hold counts...
          </div>
        ) : (
          <Row className="g-3 align-items-center">
            <Col xs={12} lg={5}>
              <div className="position-relative d-flex justify-content-center">
                <BasicECharts
                  echarts={echarts}
                  options={getOptions(getThemeColor, chartData, total, hasData)}
                  style={{ width: '100%', minHeight: '13rem' }}
                />
              </div>
            </Col>

            <Col xs={12} lg={7}>
              <Row className="g-2 mb-3">
                <Col xs={6}>
                  <div className="rounded-2 bg-100 p-3 h-100">
                    <div className="fs-11 text-500 text-uppercase fw-semibold">
                      Cycle holds
                    </div>
                    <div className="d-flex align-items-end justify-content-between">
                      <div className="fs-4 fw-bold text-info lh-1">
                        {cycleCount}
                      </div>
                      <div className="fs-11 text-600">{cyclePercent}%</div>
                    </div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="rounded-2 bg-100 p-3 h-100">
                    <div className="fs-11 text-500 text-uppercase fw-semibold">
                      Non-cycle
                    </div>
                    <div className="d-flex align-items-end justify-content-between">
                      <div className="fs-4 fw-bold text-warning lh-1">
                        {nonCycleCount}
                      </div>
                      <div className="fs-11 text-600">
                        {total > 0 ? 100 - cyclePercent : 0}%
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="fw-semibold text-900">Store concentration</div>
                {topStore && selectedStore.value === 'ALL' && (
                  <Badge bg="warning" className="text-dark rounded-pill fs-11">
                    Highest: {topStore.storeName}
                  </Badge>
                )}
              </div> */}

              {byStore.length === 0 ? (
                <div className="text-center py-4 text-500 bg-100 rounded-2">
                  No active drug holds found.
                </div>
              ) : (
                byStore.map(store => (
                  <StoreRow
                    key={store.store_id}
                    store={store}
                    total={toNumber(data.total)}
                  />
                ))
              )}

              <div className="border-top mt-2 pt-2 d-flex flex-wrap gap-2 fs-11 text-600">
                {selectedStoreBreakdown.map(item => (
                  <span key={item.is_cycle}>
                    <FontAwesomeIcon
                      icon="circle"
                      className={
                        Number(item.is_cycle) === 1
                          ? 'me-1 text-info'
                          : 'me-1 text-warning'
                      }
                    />
                    {getCycleLabel(item.is_cycle)}:{' '}
                    <strong>{item.count}</strong>
                  </span>
                ))}
              </div>
            </Col>
          </Row>
        )}
      </Card.Body>
    </Card>
  );
};

HoldCountsWidget.propTypes = {
  data: PropTypes.shape({
    total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    byStore: PropTypes.array,
    byIsCycle: PropTypes.array,
    breakdown: PropTypes.array
  }),
  isLoading: PropTypes.bool
};

export default HoldCountsWidget;
